import notes from '../../data/notes.json';
import type { AppDef, AppContext } from '../../types/window';
import type { Note } from '../../types/content';
import { el, button } from './dom';

const catalog = notes as Note[];

export function defaultNoteId(): string {
  return catalog[0]?.id ?? 'todo';
}

export function noteIndex(id: unknown): number {
  if (typeof id === 'string' && id) {
    const i = catalog.findIndex((n) => n.id === id);
    if (i >= 0) return i;
  }
  return 0;
}

function paperClass(paper: Note['paper']): string {
  if (paper === 'yellow') return 'is-yellow';
  if (paper === 'pink') return 'is-pink';
  return 'is-white';
}

const syncers = new WeakMap<HTMLElement, (ctx: AppContext) => void>();

function paint(root: HTMLElement, ctx: AppContext): void {
  const index = noteIndex(ctx.payload?.noteId);
  const note = catalog[index] ?? {
    id: 'empty',
    title: 'Notepad',
    body: 'No notes yet.',
    paper: 'white' as const,
  };
  ctx.setTitle(note.title);

  const shell = el('div', { className: 'notepad-app' });

  const menu = el('div', { className: 'notepad-menubar' });
  const fileWrap = el('div', { className: 'notepad-file' });
  const fileBtn = button('File', () => {
    menuBox.hidden = !menuBox.hidden;
  }, 'notepad-menu-btn');
  fileBtn.setAttribute('aria-haspopup', 'menu');
  const menuBox = el('div', { className: 'notepad-file-menu' });
  menuBox.hidden = true;
  menuBox.setAttribute('role', 'menu');
  catalog.forEach((n, i) => {
    const item = button(n.title, () => {
      menuBox.hidden = true;
      ctx.openApp('notepad', { noteId: n.id });
    }, 'notepad-file-item');
    item.setAttribute('role', 'menuitem');
    if (i === index) item.classList.add('is-current');
    menuBox.append(item);
  });
  fileWrap.append(fileBtn, menuBox);
  menu.append(fileWrap, el('span', { className: 'notepad-menu-ghost', text: 'Edit' }));

  const body = el('div', {
    className: `notepad-body ${paperClass(note.paper)}`,
    text: note.body,
  });

  const pager = el('div', { className: 'notepad-pager' });
  const prev = button('◀', () => {
    if (!catalog.length) return;
    const next = (index - 1 + catalog.length) % catalog.length;
    ctx.openApp('notepad', { noteId: catalog[next].id });
  }, 'notepad-page-btn');
  prev.disabled = catalog.length < 2;
  prev.setAttribute('aria-label', 'Previous note');
  pager.append(prev);
  catalog.forEach((n, i) => {
    const b = button(String(i + 1), () => ctx.openApp('notepad', { noteId: n.id }), 'notepad-page-num');
    b.setAttribute('aria-label', n.title);
    if (i === index) b.classList.add('is-current');
    pager.append(b);
  });
  const nxt = button('▶', () => {
    if (!catalog.length) return;
    const next = (index + 1) % catalog.length;
    ctx.openApp('notepad', { noteId: catalog[next].id });
  }, 'notepad-page-btn');
  nxt.disabled = catalog.length < 2;
  nxt.setAttribute('aria-label', 'Next note');
  pager.append(nxt);

  shell.append(menu, body, pager);
  root.replaceChildren(shell);

  const hide = (e: PointerEvent) => {
    if (!(e.target as HTMLElement).closest('.notepad-file')) menuBox.hidden = true;
  };
  document.addEventListener('pointerdown', hide);
  shell.dataset.bound = '1';
  (shell as HTMLElement & { _hide?: (e: PointerEvent) => void })._hide = hide;
}

export const notepadApp: AppDef = {
  appId: 'notepad',
  title: 'Notepad',
  icon: 'notepad',
  defaultSize: { w: 440, h: 360 },
  minSize: { w: 280, h: 220 },
  singleton: true,
  mount(root, ctx) {
    const sync = (next: AppContext) => paint(root, next);
    syncers.set(root, sync);
    sync(ctx);
    return () => {
      const hide = (root.firstElementChild as HTMLElement & { _hide?: (e: PointerEvent) => void })?._hide;
      if (hide) document.removeEventListener('pointerdown', hide);
      syncers.delete(root);
      root.replaceChildren();
    };
  },
  update(root, ctx) {
    const hide = (root.firstElementChild as HTMLElement & { _hide?: (e: PointerEvent) => void })?._hide;
    if (hide) document.removeEventListener('pointerdown', hide);
    syncers.get(root)?.(ctx);
  },
};
